import { TASK_KIND_LABELS, TASK_STATUS_LABELS, formatDateTime, type TaskKind } from '@takumi/shared';
import { CalendarCheck, Check, Plus, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/form';
import { Badge, Card, EmptyState, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useInvalidateCrm, useTasks, useUserOptions } from '@/lib/queries';
import type { Task } from '@/lib/types';
import { cn } from '@/lib/utils';
import { FollowupBadge, urgencyAccent } from '@/features/crm/badges';
import { ContactActions } from '@/features/crm/ContactActions';
import { CompleteTaskDialog, NewTaskDialog } from '@/features/crm/TaskDialogs';

export function TasksPage() {
  const [assigneeId, setAssigneeId] = useState('me');
  const [status, setStatus] = useState('open');
  const [kind, setKind] = useState('');
  const [creating, setCreating] = useState(false);
  const users = useUserOptions();
  const { data, isLoading } = useTasks({ assigneeId, status, kind: kind || undefined });

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="משימות ופולואפים"
        subtitle={data ? `${data.length} ${status === 'open' ? 'פתוחות' : 'פריטים'}` : ' '}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> חדש
          </Button>
        }
      />
      <Card className="mb-4 flex flex-wrap gap-2 p-3">
        <Select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className="w-40" aria-label="אחראי">
          <option value="me">שלי</option>
          <option value="all">כל הצוות</option>
          {users.data?.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-32" aria-label="סטטוס">
          <option value="open">פתוחות</option>
          <option value="done">בוצעו</option>
          <option value="cancelled">בוטלו</option>
          <option value="all">הכל</option>
        </Select>
        <Select value={kind} onChange={(e) => setKind(e.target.value)} className="w-36" aria-label="סוג">
          <option value="">כל הסוגים</option>
          {(['followup', 'meeting', 'task', 'call'] as TaskKind[]).map((k) => <option key={k} value={k}>{TASK_KIND_LABELS[k]}</option>)}
        </Select>
      </Card>
      <Card className="overflow-hidden">
        {isLoading && <div className="space-y-3 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>}
        {data?.length === 0 && <EmptyState icon={<CalendarCheck />} title="אין משימות להצגה" />}
        <ul className="divide-y divide-line/70">
          {data?.map((t) => <TaskItem key={t.id} task={t} />)}
        </ul>
      </Card>
      <NewTaskDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function TaskItem({ task }: { task: Task }) {
  const [completing, setCompleting] = useState(false);
  const invalidate = useInvalidateCrm();
  const reopen = useMutation({
    mutationFn: () => api.post(`/tasks/${task.id}/reopen`),
    onSuccess: () => invalidate(task.organizationId),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const open = task.status === 'open';
  return (
    <li className={cn('relative flex items-center gap-3 px-4 py-3 before:absolute before:inset-y-0 before:start-0 before:w-[3px]', open && urgencyAccent(task.dueAt, false))}>
      <Badge variant="outline">{TASK_KIND_LABELS[task.kind]}</Badge>
      <div className="min-w-0 flex-1">
        <div className={cn('font-semibold', !open && 'text-muted line-through')}>
          {task.organizationId ? (
            <Link to={`/org/${task.organizationId}`} className="hover:underline">
              {task.organizationName}
            </Link>
          ) : (
            task.title
          )}
        </div>
        <div className="truncate text-xs text-muted">
          {[task.organizationId ? task.title : null, formatDateTime(task.dueAt), task.contactName, task.assigneeName, task.outcome].filter(Boolean).join(' · ')}
        </div>
      </div>
      {open ? <FollowupBadge dueAt={task.dueAt} compact /> : <Badge>{TASK_STATUS_LABELS[task.status]}</Badge>}
      <ContactActions phone={task.contactPhone} whatsapp={task.contactWhatsapp} />
      {open ? (
        <Button size="sm" variant="secondary" onClick={() => setCompleting(true)}>
          <Check /> בוצע
        </Button>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => reopen.mutate()} loading={reopen.isPending}>
          <RotateCcw /> פתיחה מחדש
        </Button>
      )}
      <CompleteTaskDialog task={task} open={completing} onOpenChange={setCompleting} />
    </li>
  );
}
